import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Shield, Heart, Brain, CheckCircle, Calendar, ArrowRight, AlertCircle } from 'lucide-react';
import CTA from '../components/CTA';

const faqs = [
  {
    question: 'Is needle phobia common?',
    answer: 'Extremely common — estimated 1 in 10 people have significant needle phobia, but many avoid treatment out of embarrassment. You are not alone.',
  },
  {
    question: 'What about the fainting response — can hypnotherapy help with that?',
    answer: 'Yes. The vasovagal response (fainting) is a conditioned physiological pattern. Hypnotherapy can retrain this response alongside the anxiety.',
  },
  {
    question: "I've been putting off medical care because of needle phobia — is it too late?",
    answer: "It's never too late. Many clients come to Sharon having avoided blood tests for years. Most are able to complete routine procedures after just 2-3 sessions.",
  },
  {
    question: 'How many sessions for needle phobia hypnotherapy?',
    answer: 'Most clients are significantly improved in 2-4 sessions. Specific phobias often respond faster than generalised anxiety.',
  },
];

const NeedlePhobiaHypnotherapy: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Needle Phobia Hypnotherapy Belfast | Fear of Injections | Sharon Clare NI</title>
        <meta
          name="description"
          content="Hypnotherapy for needle phobia and fear of injections in Belfast and Northern Ireland. Overcome trypanophobia so blood tests, vaccines, and medical procedures feel manageable."
        />
        <meta
          name="keywords"
          content="needle phobia hypnotherapy Belfast, fear of injections hypnotherapy, trypanophobia hypnotherapy, blood test phobia hypnotherapy Northern Ireland"
        />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/needle-phobia-hypnotherapy/" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faqs.map((faq) => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: faq.answer,
            },
          })),
        })}</script>
      </Helmet>

      {/* Hero */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom text-center max-w-4xl mx-auto">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-4">
            Needle Phobia Hypnotherapy in Northern Ireland
          </h1>
          <p className="text-xl font-heading text-gray-700 mb-8">
            Overcome the fear of needles, injections, and blood tests. Stop avoiding essential medical care — most clients see real improvement in just 2-4 sessions.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/book"
              className="btn-primary inline-flex items-center justify-center"
            >
              <Calendar className="w-5 h-5 mr-2" /> Request a 10-Minute Call
            </a>
            <a href="tel:07932139245" className="btn-secondary inline-flex items-center justify-center">
              <ArrowRight className="w-5 h-5 mr-2" /> Call 07932 139245
            </a>
          </div>
        </div>
      </section>

      {/* Section 2 — Symptoms */}
      <section className="py-16 bg-white">
        <div className="container-custom max-w-5xl">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6">
            More Than Just a Fear of Needles
          </h2>
          <div className="grid md:grid-cols-2 gap-4 mb-8">
            {[
              'Fainting or near-fainting at the sight or thought of needles',
              'Extreme anxiety in the days or weeks before a blood test or injection',
              'Avoiding essential medical care — skipping vaccinations, blood tests, or dental work',
              'Embarrassment at a fear that feels disproportionate to the situation',
              'Vasovagal response — dizziness, nausea, or loss of consciousness',
            ].map((symptom) => (
              <div key={symptom} className="flex items-start space-x-3 bg-accent-50 rounded-xl p-4">
                <AlertCircle className="w-5 h-5 text-accent-600 mt-0.5 flex-shrink-0" />
                <p className="text-gray-700">{symptom}</p>
              </div>
            ))}
          </div>
          <p className="text-gray-700 leading-relaxed">
            Needle phobia (trypanophobia) affects an estimated 1 in 10 people. It is not a weakness or something you should be embarrassed about — it is a conditioned response, often with a strong physiological component (the vasovagal fainting response). The real danger of needle phobia is not the fear itself, but the medical care people avoid because of it.
          </p>
        </div>
      </section>

      {/* Section 3 — How Hypnotherapy Helps */}
      <section className="py-16 bg-gradient-to-br from-accent-50 to-primary-50">
        <div className="container-custom max-w-5xl">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6">
            How Hypnotherapy Helps Needle Phobia
          </h2>
          <p className="text-gray-700 leading-relaxed mb-8">
            Clinical hypnotherapy uses systematic desensitisation to reduce the phobic response gradually and safely. By working with the subconscious mind, Sharon can help you build a calm, controlled association with needles and injections — replacing the automatic fear response with a sense of confidence. For clients who experience the vasovagal (fainting) response, hypnotherapy can address the underlying physiological pattern directly, retraining the nervous system's reaction alongside the anxiety.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: <Shield className="w-6 h-6 text-accent-600" />, title: 'Desensitise the phobic response to needles' },
              { icon: <Heart className="w-6 h-6 text-accent-600" />, title: 'Build a calm, controlled response to injections and blood tests' },
              { icon: <Brain className="w-6 h-6 text-accent-600" />, title: 'Address the vasovagal (fainting) pattern specifically' },
              { icon: <CheckCircle className="w-6 h-6 text-green-600" />, title: 'Most people need just 2-4 sessions' },
            ].map(({ icon, title }) => (
              <div key={title} className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 mt-0.5">{icon}</div>
                  <h3 className="font-heading font-semibold text-gray-900">{title}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 4 — FAQ */}
      <section className="py-16 bg-white">
        <div className="container-custom max-w-4xl">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-8">
            Common Questions About Needle Phobia Hypnotherapy
          </h2>
          <div className="space-y-6">
            {faqs.map((faq) => (
              <div key={faq.question} className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
                <h3 className="font-heading font-semibold text-gray-900 mb-3">{faq.question}</h3>
                <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 5 — Pillar link */}
      <section className="py-8 bg-white">
        <div className="container-custom max-w-4xl text-center">
          <a href="/medical-anxiety-hypnotherapy" className="text-accent-600 hover:text-accent-700 font-semibold inline-flex items-center gap-1">
            Learn more about medical procedure anxiety <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </section>

      {/* Section 6 — CTA */}
      <CTA
        title="Ready to Overcome Needle Phobia?"
        subtitle="Book your initial consultation. Most clients are able to complete blood tests and injections comfortably after just a few sessions."
        primaryButtonText="Request a 10-Minute Call"
        primaryButtonHref="/book"
        secondaryButtonText="Call Now"
        secondaryButtonHref="tel:07932139245"
        variant="accent"
      />
    </main>
  );
};

export default NeedlePhobiaHypnotherapy;
